import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, PoundSterling, Clock, Truck, MessageCircle, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { setAppUnreadBadge, clearAppUnreadBadge } from "@/lib/push";

/**
 * Iteration 250 — Parent Alert Bell.
 *
 * variant="bell" renders the compact icon button (desktop top-nav,
 * mobile dashboard pill row); variant="row" renders the full-width
 * pill used inside the mobile hamburger drawer. Both open the same
 * recent-activity panel (sales / approvals / shipping / messages) with
 * direct links, and opening the panel marks everything read — clearing
 * the bell count AND the installed Home Screen app badge.
 */
const KIND_META = {
  sale: { Icon: PoundSterling, cls: "bg-emerald-100 text-emerald-700" },
  approval: { Icon: Clock, cls: "bg-amber-100 text-amber-800" },
  shipping: { Icon: Truck, cls: "bg-sky-100 text-sky-700" },
  message: { Icon: MessageCircle, cls: "bg-brandPink/10 text-brandPink" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function NotificationBell({ testIdPrefix = "notification-bell", variant = "bell" }) {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // Iteration 314 — on viewports ≤768px the bell bypasses the clipped
  // inline dropdown and opens a full-screen notifications sheet (same
  // data + actions as the hamburger drawer's Notifications pill).
  const [mobileSheet, setMobileSheet] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      const n = Number(data?.unread_count || 0);
      setUnread(n);
      setAppUnreadBadge(n);
    } catch { /* signed-out / offline — stay silent */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 45000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Other surfaces (e.g. the Orders dashboard) can mark order alerts
  // read — keep the bell in sync when they do.
  useEffect(() => {
    const handler = () => refreshCount();
    window.addEventListener("mtt:notifications-cleared", handler);
    return () => window.removeEventListener("mtt:notifications-cleared", handler);
  }, [refreshCount]);

  // Close the panel on outside tap / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const togglePanel = async () => {
    if (open) { setOpen(false); return; }
    // Iteration 314 — mobile override: ≤768px opens the full-screen sheet.
    setMobileSheet(variant === "bell" && window.innerWidth <= 768);
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      // Viewing the panel clears the bell count + Home Screen badge.
      await api.post("/notifications/mark-read", {});
      setUnread(0);
      clearAppUnreadBadge();
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  const go = (url) => { setOpen(false); navigate(url || "/orders"); };

  // Shared panel body — identical content for the desktop dropdown and
  // the iteration-314 mobile full-screen sheet.
  const panelBody = (
    <>
      {loading ? (
        <div className="py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500" data-testid={`${testIdPrefix}-empty`}>
          No notifications yet — sales, approvals and shipping updates will appear here.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
          {items.map((n, i) => {
            const meta = KIND_META[n.kind] || KIND_META.message;
            const Icon = meta.Icon;
            return (
              <button
                key={n.id || i}
                type="button"
                onClick={() => go(n.url)}
                className="w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-xl hover:bg-slate-50 transition"
                data-testid={`${testIdPrefix}-item-${i}`}
              >
                <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${meta.cls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-bold text-slate-900 truncate">{n.title}</span>
                  <span className="block text-[11px] text-slate-600 line-clamp-2">{n.body}</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className={`relative ${variant === "row" ? "w-full" : ""}`} ref={wrapRef}>
      <button
        type="button"
        onClick={togglePanel}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        className={variant === "row"
          ? "inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition w-full"
          : "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-slate-200 text-brandSlate hover:border-brandPink hover:text-brandPink transition"}
        data-testid={`${testIdPrefix}-btn`}
      >
        <Bell className="w-4 h-4" />
        {variant === "row" && <span>Notifications</span>}
        {unread > 0 && (
          <span
            className={variant === "row"
              ? "ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brandPink text-white text-[10px] font-bold"
              : "absolute -top-1 -right-1 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-brandPink text-white text-[9px] font-bold"}
            data-testid={`${testIdPrefix}-unread-badge`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && mobileSheet && (
        /* Iteration 314 — mobile full-screen notifications sheet. Tapping
           the dimmed backdrop or the X closes it; the sheet itself is
           scrollable and clears the fixed navbar. */
        <div
          className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testIdPrefix}-mobile-overlay`}
        >
          <div
            className="absolute inset-x-0 bottom-0 top-16 bg-white rounded-t-3xl shadow-pop flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testIdPrefix}-mobile-panel`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="font-display font-semibold text-brandSlate">Recent activity</div>
              <div className="flex items-center gap-3">
                <Link
                  to="/orders"
                  onClick={() => setOpen(false)}
                  className="text-[11px] font-bold text-brandTeal hover:underline"
                  data-testid={`${testIdPrefix}-view-orders-mobile`}
                >
                  View orders
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  className="inline-flex items-center justify-center w-11 h-11 -mr-2 text-slate-400 hover:text-slate-700"
                  data-testid={`${testIdPrefix}-mobile-close`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">{panelBody}</div>
          </div>
        </div>
      )}
      {open && !mobileSheet && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] card-base p-3 shadow-pop z-50 bg-white"
          data-testid={`${testIdPrefix}-panel`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-semibold text-brandSlate text-sm">Recent activity</div>
            <Link
              to="/orders"
              onClick={() => setOpen(false)}
              className="text-[11px] font-bold text-brandTeal hover:underline"
              data-testid={`${testIdPrefix}-view-orders`}
            >
              View orders
            </Link>
          </div>
          {panelBody}
        </div>
      )}
    </div>
  );
}
